import { calculateLocalTimeConditionDelta, calculateTimeoutOnCurrentPageConditionDelta, checkUrlCondition, } from "./audience-check";
export var TargetingErrorCodes;
(function (TargetingErrorCodes) {
    TargetingErrorCodes["WHERE_NOT_MATCHING"] = "WHERE_NOT_MATCHING";
    TargetingErrorCodes["WHEN_NOT_MATCHING"] = "WHEN_NOT_MATCHING";
    TargetingErrorCodes["WHO_NOT_MATCHING"] = "WHO_NOT_MATCHING";
    TargetingErrorCodes["NO_SECTIONS"] = "NO_SECTIONS";
    TargetingErrorCodes["NOT_GENERATED"] = "NOT_GENERATED";
    TargetingErrorCodes["NOT_TARGETED"] = "NOT_TARGETED";
    TargetingErrorCodes["NOT_PUBLISHED"] = "NOT_PUBLISHED";
    TargetingErrorCodes["DISMISSED"] = "DISMISSED";
    TargetingErrorCodes["FINISHED"] = "FINISHED";
    TargetingErrorCodes["EXPIRED"] = "EXPIRED";
    TargetingErrorCodes["TARGETING_DISABLED"] = "TARGETING_DISABLED";
})(TargetingErrorCodes || (TargetingErrorCodes = {}));
export class ClientTargetingEvaluator {
    url;
    selectorObserver;
    localTime;
    firstTimeVisitedAt;
    constructor({ url, selectorObserver, localTime, firstTimeVisitedAt, }) {
        this.url = url;
        this.selectorObserver = selectorObserver;
        this.localTime = localTime;
        this.firstTimeVisitedAt = firstTimeVisitedAt;
    }
    async evaluateGuide(guide) {
        const whereMatchResults = await this.checkWhereConditions(guide.targeting, guide.settings);
        const isWhereMatching = whereMatchResults.every((match) => match.result);
        const whereMatch = {
            predicates: whereMatchResults,
            result: isWhereMatching,
            message: isWhereMatching ? undefined : TargetingErrorCodes.WHERE_NOT_MATCHING,
        };
        const whenMatchResults = await this.checkWhenConditions(guide.targeting);
        const isWhenMatching = whenMatchResults.every((match) => match.result);
        const whenMatch = {
            predicates: whenMatchResults,
            result: isWhenMatching,
            message: isWhenMatching ? undefined : TargetingErrorCodes.WHEN_NOT_MATCHING,
        };
        const res = {
            ...guide.matchEvaulation,
            whereMatch,
            whenMatch,
            isTargetingEnabled: guide.targeting.isEnabled,
            isWhereTargeted: whereMatch.result,
            isWhenTargeted: whenMatch.result,
            result: guide.matchEvaulation.prefilter.result && whereMatch.result && whenMatch.result,
            message: guide.matchEvaulation.prefilter.message ?? whereMatch.message ?? whenMatch.message,
        };
        return res;
    }
    async evaluateChecklist(checklist) {
        const whereMatchResults = await this.checkWhereConditions(checklist.targeting, {});
        const isWhereMatching = whereMatchResults.every((match) => match.result);
        const whereMatch = {
            predicates: whereMatchResults,
            result: isWhereMatching,
            message: isWhereMatching ? undefined : TargetingErrorCodes.WHERE_NOT_MATCHING,
        };
        const res = {
            ...checklist.matchEvaluation,
            whereMatch,
            isTargetingEnabled: checklist.targeting.isEnabled,
            isWhereTargeted: whereMatch.result,
            result: checklist.matchEvaluation.prefilter.result && whereMatch.result,
            message: checklist.matchEvaluation.prefilter.message ?? whereMatch.message,
        };
        return res;
    }
    async checkWhereConditions(targeting, settings) {
        const conditions = (targeting?.where ?? []);
        const matchResults = [];
        for (const condition of conditions) {
            if (condition.left_operand === "url") {
                const isUrlConditionSatisfied = checkUrlCondition(condition, this.url);
                matchResults.push({
                    predicate: condition,
                    result: isUrlConditionSatisfied,
                    value: this.url,
                });
            }
            else if (condition.left_operand === "selector") {
                const selector = condition.right_operand;
                if (condition.operator === "not_exists") {
                    const timeout = settings.selector_timeout_settings?.not_exist_timeout ?? 3000;
                    const isElementExists = await this.selectorObserver.isAvailable(selector, timeout);
                    matchResults.push({
                        predicate: condition,
                        result: !isElementExists,
                        value: selector,
                    });
                }
                else if (condition.operator === "exists") {
                    const timeout = settings.selector_timeout_settings?.exist_timeout ?? 3000;
                    const isElementExists = await this.selectorObserver.isAvailable(selector, timeout);
                    matchResults.push({
                        predicate: condition,
                        result: isElementExists,
                        value: selector,
                    });
                }
            }
        }
        return matchResults;
    }
    // Filter out guides that we don't need to evaluate anymore
    static prefilterGuide(params) {
        const { guide, dismissedGuideIds, finishedGuideIds, expiredGuideIds } = params;
        if (!guide.matchEvaulation.isGenerated) {
            return { result: false, message: TargetingErrorCodes.NOT_GENERATED };
        }
        if (!guide.matchEvaulation.isWhoTargeted) {
            return { result: false, message: TargetingErrorCodes.WHO_NOT_MATCHING };
        }
        if (!guide.matchEvaulation.isPublished) {
            return { result: false, message: TargetingErrorCodes.NOT_PUBLISHED };
        }
        if (dismissedGuideIds.includes(guide.id)) {
            return { result: false, message: TargetingErrorCodes.DISMISSED, params: { dismissedGuideIds, finishedGuideIds } };
        }
        if (finishedGuideIds.includes(guide.id)) {
            return { result: false, message: TargetingErrorCodes.FINISHED, params: { dismissedGuideIds, finishedGuideIds } };
        }
        if (expiredGuideIds.includes(guide.id)) {
            return {
                result: false,
                message: TargetingErrorCodes.EXPIRED,
                params: { dismissedGuideIds, finishedGuideIds, expiredGuideIds },
            };
        }
        if (!guide.targeting.isEnabled) {
            return { result: false, message: TargetingErrorCodes.TARGETING_DISABLED };
        }
        return {
            result: true,
            message: undefined,
            params: { dismissedGuideIds, finishedGuideIds, expiredGuideIds },
        };
    }
    static prefilterChecklist(params) {
        const { checklist, dismissedChecklistIds, finishedGuideIds } = params;
        if (dismissedChecklistIds.includes(checklist.id)) {
            return { result: false, message: TargetingErrorCodes.DISMISSED };
        }
        if (!checklist.matchEvaluation.isPublished) {
            return { result: false, message: TargetingErrorCodes.NOT_PUBLISHED };
        }
        if (!checklist.matchEvaluation.isWhoTargeted) {
            return { result: false, message: TargetingErrorCodes.WHO_NOT_MATCHING };
        }
        const checklistGuideIds = checklist.items.map((item) => item.guide_id);
        if (checklistGuideIds.every((id) => finishedGuideIds.includes(id))) {
            return { result: false, message: TargetingErrorCodes.FINISHED };
        }
        if (!checklist.targeting.isEnabled) {
            return { result: false, message: TargetingErrorCodes.TARGETING_DISABLED };
        }
        return {
            result: true,
            message: undefined,
            params: { dismissedChecklistIds, finishedGuideIds },
        };
    }
    async checkWhenConditions(targeting) {
        const conditions = (targeting?.when ?? []);
        const matchResults = [];
        for (const condition of conditions) {
            if (condition.left_operand === "local_time_at") {
                const timeDelta = calculateLocalTimeConditionDelta(condition, this.localTime);
                switch (condition.operator) {
                    case "before":
                        matchResults.push({
                            predicate: condition,
                            result: timeDelta > 0,
                            value: timeDelta.toString(),
                        });
                        break;
                    case "after":
                        matchResults.push({
                            predicate: condition,
                            result: timeDelta <= 0,
                            value: timeDelta.toString(),
                        });
                        break;
                }
            }
            else if (condition.left_operand === "time_on_current_page") {
                const timeDelta = calculateTimeoutOnCurrentPageConditionDelta(condition, this.firstTimeVisitedAt, this.localTime);
                matchResults.push({
                    predicate: condition,
                    result: timeDelta >= condition.right_operand * 1000,
                    value: timeDelta.toString(),
                });
            }
        }
        return matchResults;
    }
}
